/**
 * /reset?token=...&email=... -- magic-link reset landing page.
 *
 * User pastes/clicks the link issued by /api/users/:id/issue-reset-link.
 * Client derives a new KDF + ASK + ECDH keypair + metadata key + master
 * password hash and POSTs /api/auth/confirm-reset. Vault items wrapped to
 * the user's old ECDH pubkey become unreadable (expected) until SU
 * re-wraps them under the new pubkey.
 */

import { html, nothing, type TemplateResult } from "lit-html";

import { api, ApiError } from "../api.js";
import { bootstrapGenesisLocal, computeMasterPasswordHash } from "../client-crypto.js";
import { rerender, setRoute, showToast, state as appState } from "../main.js";
import { toBase64 } from "@shared/crypto/bytes.js";

const s = { password: "", password2: "", busy: false, err: "", done: false };

async function submit(): Promise<void> {
  if (s.busy) return;
  if (s.password.length < 12) {
    s.err = appState.lang === "en" ? "Password must be at least 12 chars" : "パスワードは 12 文字以上必要です";
    rerender(); return;
  }
  if (s.password !== s.password2) {
    s.err = appState.lang === "en" ? "Passwords do not match" : "パスワードが一致しません";
    rerender(); return;
  }
  s.busy = true; s.err = ""; rerender();
  const url = new URL(window.location.href);
  const token = url.searchParams.get("token") ?? "";
  const email = url.searchParams.get("email") ?? "";
  if (!token || !email) {
    s.err = appState.lang === "en" ? "Missing token or email in URL" : "URL にトークンまたはメールがありません";
    s.busy = false; rerender(); return;
  }
  try {
    // Reuse the genesis bootstrap helper to derive a full fresh keypair
    // bundle. We only consume kdf_salt + kdf_params + wrapped_ask + mph
    // from it; the rest (ECDH, metadata, signing) would require server
    // re-wrap of items to be usable, which the SU does after reset.
    const boot = await bootstrapGenesisLocal({
      email,
      personal_email: "noop@noop.com",
      name: "reset",
      password: s.password,
    });
    const kdfParams = JSON.parse(boot.body.kdf_params_json as string);
    void kdfParams;
    const mphB64 = boot.body.master_password_hash_b64 as string;
    const wrappedAskB64 = boot.body.wrapped_ask_b64 as string;
    const kdfSaltB64 = boot.body.kdf_salt_b64 as string;
    const kdfParamsJson = boot.body.kdf_params_json as string;

    void computeMasterPasswordHash; // re-export safety, mph already computed in boot
    void toBase64;

    await api.confirmReset({
      reset_token_b64url: token,
      email,
      new_kdf_salt_b64: kdfSaltB64,
      new_kdf_params_json: kdfParamsJson,
      new_wrapped_ask_b64: wrappedAskB64,
      new_master_password_hash_b64: mphB64,
    });
    s.done = true;
    showToast(appState.lang === "en" ? "Password reset complete" : "パスワードリセット完了");
  } catch (err) {
    s.err = err instanceof ApiError ? `${err.code}: ${err.message}` : (err as Error).message;
  } finally {
    s.busy = false;
    rerender();
  }
}

export function renderReset(): TemplateResult {
  if (s.done) {
    return html`
      <div class="login-shell">
        <div class="login-card">
          <h1>
            <span data-lang="en">Password reset complete</span><span data-lang="jp">パスワードリセット完了</span>
          </h1>
          <p>
            <span data-lang="en">You can now log in with your new password.</span><span data-lang="jp">新しいパスワードでログインできるようになりました。</span>
          </p>
          <p style="color: var(--status-pending); font-size: 13px;">
            <span data-lang="en">Note: previously stored vault items remain inaccessible until the Super User re-wraps them under your new keypair. Ask the SU to run the rewrap from the admin console.</span><span data-lang="jp">注意: 既存の Vault アイテムは、Super User が新しい鍵ペアで再ラップするまでアクセスできません。Super User に管理コンソールから再ラップの実行を依頼してください。</span>
          </p>
          <button class="primary" style="width: 100%; margin-top: 16px;"
            @click=${() => setRoute("login")}>
            <span data-lang="en">Go to login</span><span data-lang="jp">ログインへ</span>
          </button>
        </div>
      </div>
    `;
  }
  return html`
    <div class="login-shell">
      <div class="login-card">
        <h1>
          <span data-lang="en">Reset password</span><span data-lang="jp">パスワードリセット</span>
        </h1>
        <p style="color: var(--text-2); font-size: 13px;">
          <span data-lang="en">Choose a new master password (12+ characters).</span><span data-lang="jp">新しいマスターパスワード (12 文字以上) を入力してください。</span>
        </p>
        <div class="form-row">
          <label>
            <span data-lang="en">New password</span><span data-lang="jp">新しいパスワード</span>
          </label>
          <input type="password" .value=${s.password}
            @input=${(e: InputEvent) => (s.password = (e.target as HTMLInputElement).value)} />
        </div>
        <div class="form-row">
          <label>
            <span data-lang="en">Confirm new password</span><span data-lang="jp">新しいパスワード (確認)</span>
          </label>
          <input type="password" .value=${s.password2}
            @input=${(e: InputEvent) => (s.password2 = (e.target as HTMLInputElement).value)} />
        </div>
        ${s.err ? html`<p style="color: var(--status-error); font-size: 13px;">${s.err}</p>` : nothing}
        <button class="primary" style="width: 100%; margin-top: 8px;" ?disabled=${s.busy}
          @click=${() => void submit()}>
          ${s.busy
            ? html`<span data-lang="en">Working…</span><span data-lang="jp">処理中…</span>`
            : html`<span data-lang="en">Reset password</span><span data-lang="jp">パスワードをリセット</span>`}
        </button>
      </div>
    </div>
  `;
}
